
import React, { useRef, useEffect, useState, ReactNode } from 'react';

interface ScrollAnimationProps {
  children: ReactNode;
  animation?: 'fadeIn' | 'slideInUp' | 'scaleIn';
  delay?: number; // in milliseconds
  className?: string;
  threshold?: number;
  as?: React.ElementType; 
}

export const ScrollAnimation: React.FC<ScrollAnimationProps> = ({
  children,
  animation = 'fadeIn',
  delay = 0,
  className = '',
  threshold = 0.15, // Aumentado ligeiramente para garantir que o elemento está visível
  as: Component = 'div',
}) => {
  const ref = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold]);

  // Transições sincronizadas com o index.html (0.8s a 1s)
  const baseClasses = 'transition-all duration-1000 cubic-bezier(0.16, 1, 0.3, 1) will-change-transform will-change-opacity';
  
  const animationClasses = {
    fadeIn: {
      initial: 'opacity-0',
      visible: 'opacity-100',
    },
    slideInUp: {
      initial: 'opacity-0 translate-y-10',
      visible: 'opacity-100 translate-y-0',
    },
    scaleIn: {
      initial: 'opacity-0 scale-[0.97]',
      visible: 'opacity-100 scale-100',
    }
  };

  const currentAnimation = animationClasses[animation];

  return (
    <Component
      ref={ref}
      className={`${baseClasses} ${className} ${isVisible ? `${currentAnimation.visible} is-visible` : currentAnimation.initial}`}
      style={{ transitionDelay: isVisible ? `${delay}ms` : '0ms' }}
    >
      {children}
    </Component>
  );
};
